import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CalendarClock, Info, KeyRound, Shield, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useCreateProviderApiToken,
  useProviderApiTokens,
  useProviderSsoConfig,
  useRevokeProviderApiToken,
} from '@/hooks/provider/useProviderApiTokens';
import { ProviderApiToken } from '@/types/provider-api-token';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { analyticsService } from '@/services/analytics.service';
import { CreateProviderApiTokenDialog } from '@/components/settings/provider-api/CreateProviderApiTokenDialog';

const SCOPE_CONFIG: ReadonlyArray<{
  id: string;
  labelKey: string;
  descriptionKey: string;
}> = [
  {
    id: 'sso:issue',
    labelKey: 'settings.providerApi.scopes.ssoIssue.label',
    descriptionKey: 'settings.providerApi.scopes.ssoIssue.description',
  },
  {
    id: 'provider:access',
    labelKey: 'settings.providerApi.scopes.providerAccess.label',
    descriptionKey: 'settings.providerApi.scopes.providerAccess.description',
  },
];

function formatDate(value: string | null | undefined) {
  if (!value) {
    return '—';
  }

  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    console.warn('Failed to format date', value, error);
    return value;
  }
}

function getLastUsedLabel(value: string | null | undefined) {
  if (!value) {
    return 'Never';
  }

  try {
    return `${formatDistanceToNow(new Date(value), { addSuffix: true })}`;
  } catch (error) {
    console.warn('Failed to format distance for', value, error);
    return formatDate(value);
  }
}

export function ProviderApiAccessTab() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const featureEnabled = import.meta.env.VITE_ENABLE_PROVIDER_API_TOKENS === 'true';
  const { data, isLoading, error, refetch, isFetching } = useProviderApiTokens({ enabled: featureEnabled });
  const { data: ssoData, isLoading: isSsoLoading } = useProviderSsoConfig({ enabled: featureEnabled });
  const revokeMutation = useRevokeProviderApiToken();
  const tokens = data?.tokens ?? [];
  const ssoConfig = ssoData ?? data?.ssoConfig ?? null;
  const createMutation = useCreateProviderApiToken();
  const [scopeFilter, setScopeFilter] = useState<string>('all');

  const defaultScopeOptions = useMemo(
    () =>
      SCOPE_CONFIG.map((scope) => ({
        id: scope.id,
        label: t(scope.labelKey),
        description: t(scope.descriptionKey),
      })),
    [t],
  );

  const scopeMeta = useMemo(() => {
    const map = new Map<string, { label: string; description: string }>();
    defaultScopeOptions.forEach((scope) => map.set(scope.id, { label: scope.label, description: scope.description }));
    return map;
  }, [defaultScopeOptions]);

  const sortedTokens = useMemo(() => {
    return [...tokens].sort((a, b) => {
      const aDate = a.lastUsedAt ?? a.createdAt;
      const bDate = b.lastUsedAt ?? b.createdAt;
      return bDate.localeCompare(aDate);
    });
  }, [tokens]);

  const availableScopes = useMemo(() => {
    const tokenScopes = new Set<string>();
    tokens.forEach((token) => {
      token.scopes.forEach((scope) => tokenScopes.add(scope));
    });
    SCOPE_CONFIG.forEach((scope) => tokenScopes.add(scope.id));
    return Array.from(tokenScopes).sort();
  }, [tokens]);

  const filteredTokens = useMemo(() => {
    if (scopeFilter === 'all') {
      return sortedTokens;
    }
    return sortedTokens.filter((token) => token.scopes.includes(scopeFilter));
  }, [sortedTokens, scopeFilter]);

  const handleTokenCreated = (response: { token: string; tokenId: string }) => {
    analyticsService.track('provider_api_token_created', {
      tokenId: response.tokenId,
      scopes: ['sso:issue'],
    });
  };

  const handleRevoke = async (token: ProviderApiToken) => {
    try {
      await revokeMutation.mutateAsync(token.id);
      analyticsService.track('provider_api_token_revoked', { tokenId: token.id });
      toast.success(t('settings.providerApi.revokeSuccess', 'Token revoked.'));
    } catch (mutationError) {
      const message =
        mutationError instanceof Error
          ? mutationError.message
          : t('settings.providerApi.revokeError', 'Unable to revoke token.');
      toast.error(message);
    }
  };

  if (!featureEnabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            {t('settings.sections.apiAccess.title')}
          </CardTitle>
          <CardDescription>
            {t('settings.providerApi.unavailable', 'Provider API access will be available once your backend enables the tokens feature flag.')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              {t(
                'settings.providerApi.toggleNotice',
                'Set VITE_ENABLE_PROVIDER_API_TOKENS=true in the provider UI environment after the API endpoints are live.',
              )}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            {t('settings.sections.apiAccess.title')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.providerApi.description',
              'Generate service tokens to automate customer management and launch secure portal sessions.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              {t(
                'settings.providerApi.securityNotice',
                'Treat API tokens like passwords. Rotate regularly, store them in a secure vault, and revoke any token that may have been exposed.',
              )}
            </AlertDescription>
          </Alert>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              {t(
                'settings.providerApi.summary',
                'Tokens inherit your provider permissions. Only issue them to trusted automation platforms.',
              )}
            </div>
            <Button onClick={() => setCreateOpen(true)} disabled={createMutation.isPending}>
              {t('settings.providerApi.createToken', 'Generate service token')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5" />
            {t('settings.providerApi.ssoConfigTitle', 'Customer SSO defaults')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.providerApi.ssoConfigDescription',
              'These defaults apply when issuing one-click customer portal links.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          {ssoConfig ? (
            <>
              <div className="rounded-lg border p-4">
                <p className="text-sm font-medium text-muted-foreground">
                  {t('settings.providerApi.ssoDefaultTtl', 'Default link lifetime')}
                </p>
                <p className="text-lg font-semibold mt-1">
                  {t('settings.providerApi.minutes', {
                    count: ssoConfig.defaultTtlMinutes,
                  })}
                </p>
                {ssoConfig.maxTtlMinutes ? (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('settings.providerApi.ssoMaxTtl', 'Maximum override: {{count}} minutes', {
                      count: ssoConfig.maxTtlMinutes,
                    })}
                  </p>
                ) : null}
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm font-medium text-muted-foreground">
                  {t('settings.providerApi.ssoDomains', 'Allowed redirect domains')}
                </p>
                <div className="mt-2 space-y-1">
                  {ssoConfig.allowedRedirectDomains.length > 0 ? (
                    ssoConfig.allowedRedirectDomains.map((domain) => (
                      <Badge key={domain} variant="outline" className="text-xs">
                        {domain}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      {t('settings.providerApi.noDomains', 'Backend has not defined any allowed domains yet.')}
                    </span>
                  )}
                </div>
              </div>
              {!ssoConfig.overridesAllowed ? (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    {t(
                      'settings.providerApi.ssoOverridesDisabled',
                      'Runtime overrides are disabled. Links will always use the default TTL and redirect rules.',
                    )}
                  </AlertDescription>
                </Alert>
              ) : null}
            </>
          ) : isLoading || isSsoLoading ? (
            <>
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </>
          ) : (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t(
                  'settings.providerApi.ssoConfigError',
                  'Unable to load SSO configuration. Your tokens will still function, but defaults are unknown.',
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex items-center justify-between gap-2">
          <div>
            <CardTitle>{t('settings.providerApi.tokensTitle', 'Service tokens')}</CardTitle>
            <CardDescription>
              {t('settings.providerApi.tokensDescription', 'Manage existing tokens and revoke them when no longer needed.')}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {availableScopes.length > 0 ? (
              <Select value={scopeFilter} onValueChange={setScopeFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder={t('settings.providerApi.scopeFilterLabel', 'Scope')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t('settings.providerApi.scopeFilterAll', 'All scopes')}
                  </SelectItem>
                  {availableScopes.map((scope) => (
                    <SelectItem key={scope} value={scope}>
                      {scopeMeta.get(scope)?.label ?? scope}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
              {t('common.refresh', 'Refresh')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full" />
              ))}
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>
                  {error instanceof Error
                    ? error.message
                    : t('settings.providerApi.tokensError', 'Failed to load tokens.')}
                </span>
                <Button size="sm" variant="outline" onClick={() => refetch()}>
                  {t('common.retry', 'Retry')}
                </Button>
              </AlertDescription>
            </Alert>
          ) : filteredTokens.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              {scopeFilter === 'all'
                ? t('settings.providerApi.noTokens', 'No service tokens yet. Generate one to enable external automation.')
                : t('settings.providerApi.noTokensForScope', 'No tokens found for this scope.')}
            </div>
          ) : (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('settings.providerApi.columns.label', 'Label')}</TableHead>
                    <TableHead>{t('settings.providerApi.columns.scopes', 'Scopes')}</TableHead>
                    <TableHead>{t('settings.providerApi.columns.created', 'Created')}</TableHead>
                    <TableHead>{t('settings.providerApi.columns.lastUsed', 'Last used')}</TableHead>
                    <TableHead className="w-[120px] text-right">
                      {t('settings.providerApi.columns.actions', 'Actions')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTokens.map((token) => (
                    <TableRow key={token.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{token.label}</span>
                          <span className="text-xs text-muted-foreground">{token.id}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          {token.scopes.map((scope) => {
                            const meta = scopeMeta.get(scope);
                            return (
                              <Badge key={scope} variant="secondary" className="text-xs font-medium">
                                {meta ? meta.label : scope}
                              </Badge>
                            );
                          })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col text-sm">
                          <span>{formatDate(token.createdAt)}</span>
                          {token.expiresAt ? (
                            <span className="text-xs text-muted-foreground">
                              {t('settings.providerApi.expiresLabel', 'Expires {{date}}', {
                                date: formatDate(token.expiresAt),
                              })}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">{getLastUsedLabel(token.lastUsedAt)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn(
                                'text-destructive hover:text-destructive hover:bg-destructive/10',
                                revokeMutation.isPending && 'opacity-50 pointer-events-none',
                              )}
                            >
                              <Trash2 className="mr-1 h-4 w-4" />
                              {t('settings.providerApi.revoke', 'Revoke')}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {t('settings.providerApi.revokeTitle', 'Revoke token?')}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {t(
                                  'settings.providerApi.revokeDescription',
                                  'This token will stop working immediately. Any systems using it will lose access until you generate a new token.',
                                )}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => handleRevoke(token)}
                              >
                                {t('settings.providerApi.revokeConfirm', 'Revoke token')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateProviderApiTokenDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        availableScopes={defaultScopeOptions}
        onTokenIssued={handleTokenCreated}
        isCreating={createMutation.isPending}
        createToken={createMutation.mutateAsync}
      />
    </div>
  );
}
